import {
  Box,
  Grid,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  Divider,
  Modal,
  Fade,
  InputLabel,
} from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { makeStyles } from "@mui/styles";
import { ReactComponent as InputSearchIcon } from "../../../assets/icons/icon-feather-search.svg";
import { ReactComponent as DropDownIcon } from "../../../assets/icons/icon-angle-down.svg";
import { ReactComponent as AngleUpIcon } from "../../../assets/icons/icon-angle-up.svg";
import { ReactComponent as HashtagIcon } from "../../../assets/icons/icon-hashtag.svg";
import SettingsIcon from "@mui/icons-material/Settings";
import { getMeasuresList } from "../../../config/services/measures";
import toast from "react-hot-toast";

const useStyles = makeStyles((theme) => ({
  crossIcon: {
    "& svg": {
      fontSize: 18,
    },
  },
  bgWhite: {
    backgroundColor: "#fff",
    borderRadius: "4px",
  },
  formInput: {
    paddingLeft: 0,
    "&::placeholder": {
      paddingLeft: "-10px",
    },
  },
  searchFieldListItem: {
    paddingTop: "0.6rem",
    paddingBottom: "0.6rem",
    "&:hover": {
      background: "#f1f1f1",
      backgroundColor: "#f1f1f1",
      zIndex: 9,
    },
  },
  menuDropdownItem: {
    fontSize: "14px",
    color: "#202124",
    "&:hover": {
      backgroundColor: "#f6f6f6",
    },
  },
}));

export default function GlobalDateRange(props) {
  const { currentSheet ,selectedSheetIndex,updateSheet} = props;
  const [showFilterForm, setShowFilterForm] = useState(currentSheet?.globalFilter?.dateRangeType ? true : false);
  const [dateRangeType, setDateRangeType] = useState(null);
  const [selectedTimePeriods, setSelectedTimePeriods] = useState(currentSheet?.globalFilter?.selectedTimePeriods || []);
  const [searchReport, setSearchReport] = useState("");
  const [reportOptions, setReportOptions] = useState([]);
  const classes = useStyles();
  const dateRangeOptions = [
    { value: "DATE", label: "Date" },
    { value: "DATE_AND_TIME", label: "Date and Time" },
    { value: "TIME_PERIOD", label: "Time Periods" },
  ];
  const timePeriodOptions = [
    'Today',
    'Yesterday', 
    'This Week',
    'Last Week',
    'This Month',
    'Last Month',
    'This Quarter',
    'Last Quarter',
    'This Year',
    'Last Year',
    'Custom'
  ];

  const reportListHandler = async () => {
    if (currentSheet && currentSheet?.reports?.length > 0) {
      const uniqueDataSetIds = new Set();
      currentSheet.reports.forEach((report) => {
        if (report?.dataSetId) {
          uniqueDataSetIds.add(report.dataSetId);
        }
      });

      const dataSetFieldsMap = {};

      setDateRangeType(currentSheet?.globalFilter?.dateRangeType || "");

      const dataSetFieldsPromises = Array.from(uniqueDataSetIds).map(
        async (dataSetId) => {
          const dataSetFields = await getMeasuresList({ dataSetId });
          dataSetFieldsMap[dataSetId] = dataSetFields.result
            .filter((field) => field.type == "time")
            .map((field) => {
              return {
                fieldId: field._id,
                fieldName: `${field?.dataset?.dataSetName}.${field?.name}`,
                fieldType: field.type,
                dataType: field.dataType,
                displayName: field.displayName || field.name,
              };
            });
        }
      );

      await Promise.all(dataSetFieldsPromises);

      const reportList = currentSheet?.reports.map((report) => {
        return {
          reportId: report?.reportId,
          reportName: report?.reportName,
          dataSetId: report?.dataSetId,
          availableFields: dataSetFieldsMap[report?.dataSetId] || [],
          checked:
            currentSheet?.globalFilter?.selectedReports?.some(
              (r) => r.reportId === report?.reportId
            ) || false,
          selectedField:
            currentSheet?.globalFilter?.selectedReports?.find(
              (r) => r.reportId === report?.reportId
            )?.selectedField || {},
        };
      });
      setReportOptions(reportList);
    }
  };

  useEffect(() => {
    if (currentSheet) {
      reportListHandler();
    }
  }, [selectedSheetIndex]);

  const handleSearchReport = (e) => {
    setSearchReport(e.target.value);
  };

  const handleReportChange = (reportId) => {
    setReportOptions((prevReports) =>
      prevReports.map((report) =>
        report.reportId === reportId
          ? { ...report, checked: !report.checked, selectedField: {} }
          : report
      )
    );
  };

  const handleApplyFilter = () => {
    const timeFilterExists = currentSheet?.sheetFilter?.some(
      (filter) => filter.type === "time"
    );
    if(timeFilterExists){
    toast.dismiss();
      toast.error("Global Filter cannot be applied as time filter already exists in the sheet.");
    }
    else{
      const selectedReports = reportOptions.filter((report) => report.checked);
      const globalFilter = {
        dateRangeType: dateRangeType,
        selectedReports,
        selectedTimePeriods: selectedTimePeriods
      };
      updateSheet({sheetIndex: selectedSheetIndex, sheet: {...currentSheet, globalFilter}});
      toast.dismiss();
      toast.success("Global Date Filter applied successfully!");      
    }
  };

  const filteredReports = reportOptions.filter(
    (report) =>
      report?.reportName.toLowerCase().includes(searchReport.toLowerCase()) &&
      report?.availableFields?.length > 0
  );

  const handleGlobalFilterDropDown = () => {
    setShowFilterForm(!showFilterForm);
  };

  const handleClearFilter = () => {
    setReportOptions((prevReports) =>
      prevReports.map((report) => ({
        ...report,
        checked: false,
        selectedField: {},
      }))
    );
    updateSheet({sheetIndex: selectedSheetIndex, sheet: {...currentSheet, globalFilter: {}}});
    setDateRangeType("");
    setSearchReport("");
  };

  const handleFieldSelect = (e, report) => {
    const selectedFieldId = e.target.value;
    const selectedField = report?.availableFields?.find(
      (field) => field.fieldId === selectedFieldId
    );
    setReportOptions((prevReports) =>
      prevReports.map((r) =>
        r.reportId === report.reportId
          ? { ...r, selectedField: selectedField || {} }
          : r
      )
    );
  };

  return (
    <Box className="report-filters-section" style={{ borderRadius: "10px" }}>
      <Box
        className="filter-header"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <DateRangeIcon className="cm_measr_lbl" />
        <Grid
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "90%",
            marginLeft: "10px",
          }}
        >
          <Typography variant="h5" className="filter-heading">
            Global Date Filter
          </Typography>
          {showFilterForm ? (
            <AngleUpIcon
              className="filter-dropdown-icon"
              style={{
                margin: "auto 0",
                cursor: "pointer",
                height: "12px",
                width: "12px",
              }}
              onClick={handleGlobalFilterDropDown}
            />
          ) : (
            <DropDownIcon
              className="filter-dropdown-icon"
              style={{
                margin: "auto 0",
                cursor: "pointer",
                height: "12px",
                width: "12px",
              }}
              onClick={handleGlobalFilterDropDown}
            />
          )}
        </Grid>
      </Box>

      <Box height="100%" py={2}>
        {showFilterForm && (
          <Box
            sx={{
              mt: 2,
              backgroundColor: "#fff",
              borderRadius: "4px",
              p: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <Box mb={2}>
              <Typography
                variant="body2"
                sx={{ paddingBottom: "0.2rem", fontWeight: 600 }}
              >
                Type of Date Range *
              </Typography>
              <Select
                value={dateRangeType}
                onChange={(e) => setDateRangeType(e.target.value)}
                className="granularity_picker report_form_ui_input_select"
                size="small"
                fullWidth
                IconComponent={DropDownIcon}
                MenuProps={{
                  classes: {
                    root: `form-select-menu`,
                  },
                }}
              >
                {dateRangeOptions.map((option, i) => (
                  <MenuItem
                    className={classes.menuDropdownItem + " menu-item"}
                    key={i}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {dateRangeType === "TIME_PERIOD" && (
              <Box mb={2}>
                <Typography
                  variant="body2"
                  sx={{ paddingBottom: "0.2rem", fontWeight: 600 }}
                >
                  Select Time Periods *
                </Typography>
                <FormControl fullWidth>
                  <Select
                    multiple
                    value={selectedTimePeriods}
                    onChange={(e) => setSelectedTimePeriods(e.target.value)}
                    className="granularity_picker report_form_ui_input_select"
                    size="small"
                    IconComponent={DropDownIcon}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.slice(0,2).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                        {selected.length > 2 && (
                          <Chip
                            label={`+${selected.length - 2}`}
                            size="small"
                          />
                        )}
                      </Box>
                    )}
                    MenuProps={{
                      classes: {
                        root: `form-select-menu`,
                      },
                    }}
                  >
                    {timePeriodOptions.map((period) => (
                      <MenuItem
                        key={period}
                        value={period}
                        className={classes.menuDropdownItem + " menu-item"}
                      >
                        <Checkbox 
                          checked={selectedTimePeriods.indexOf(period) > -1}
                          size="small"
                        />
                        <ListItemText primary={period} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box mb={2}>
              <Typography
                variant="body2"
                sx={{ paddingBottom: "0.2rem", fontWeight: 600 }}
              >
                Report Applicability & Date Field Mapping
              </Typography>

              <Box width="100%" mb={1}>
                <TextField
                  onChange={handleSearchReport}
                  className="cm_rprt_setting_content report_form_ui_input"
                  type="search"
                  size="small"
                  fullWidth
                  placeholder="Search reports"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputSearchIcon className="input_search_icon" />
                      </InputAdornment>
                    ),
                    classes: {
                      input: classes.formInput,
                      root: classes.bgWhite + " form_input",
                    },
                  }}
                />
              </Box>

              <Box
                className="field_list report_filter_list height-fit max-width-100 mt-0"
                sx={{
                  maxHeight: "200px",
                  backgroundColor: "#FFFDFA",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <List component="nav" className="field_list" dense>
                  {filteredReports.length > 0 ? (
                    filteredReports.map((report) => (
                      <ListItem
                        className={classes.searchFieldListItem}
                        key={report.reportId}
                        dense
                        sx={{ px: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Checkbox
                            edge="start"
                            checked={report?.checked || false}
                            onChange={() => handleReportChange(report.reportId)}
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={report.reportName}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                        {report?.checked && (
                          <FormControl>
                            {Object.keys(report?.selectedField).length ==0 && (
                              <InputLabel
                                id="demo-simple-select-label"
                                style={{ top: "-8px" }}
                              >
                                Select Field
                              </InputLabel>
                            )}
                            <Select
                              value={report?.selectedField?.fieldId || ""}
                              onChange={(e) => handleFieldSelect(e, report)}
                              className="report_form_ui_input_select"
                              size="small"
                              IconComponent={DropDownIcon}
                              displayEmpty
                              style={{ width: "150px" }}
                              MenuProps={{
                                classes: {
                                  root: `form-select-menu`,
                                },
                              }}
                            >
                              {report.availableFields.map((field, index) => (
                                <MenuItem
                                  className={
                                    classes.menuDropdownItem + " menu-item"
                                  }
                                  key={field?.fieldId}
                                  value={field?.fieldId}
                                >
                                  {field?.displayName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText primary="No reports found" />
                    </ListItem>
                  )}
                </List>
              </Box>
            </Box>

            <Box
              sx={{
                borderRadius: "8px",
                border: "1px solid #d0d7e5",
                backgroundColor: "#f5f8ff",
                padding: "16px",
                width: "100%",
                margin: "16px auto",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, marginBottom: 1, color: "#1C3C91" }}
              >
                Active Global Date Filter Configuration
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "#124EC6", marginBottom: 0.5 }}
              >
                <strong>Date Range Type:</strong>{" "}
                {dateRangeOptions.find(
                  (option) => option.value === dateRangeType
                )?.label || "Not Selected"}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "#124EC6", marginBottom: 1.5 }}
              >
                <strong>Applied to: </strong>
                {`${
                  reportOptions.filter((report) => report.checked).length
                } reports`}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                {reportOptions
                  .filter((report) => report.checked)
                  .map((report) => (
                    <Chip
                      label={
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Typography
                            component="span"
                            sx={{
                              color: "#2043AF",
                              fontSize: "0.75rem",
                              fontWeight: "bold",
                            }}
                          >
                            {report?.reportName}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              color: "#2E6FEF",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          >
                            (
                            {report?.selectedField?.displayName ||
                              "Not Selected"}
                            )
                          </Typography>
                        </Box>
                      }
                      size="small"
                      sx={{
                        backgroundColor: "#dbe6ff",
                        borderRadius: "16px",
                        height: "24px",
                      }}
                    />
                  ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="right" gap={0.8}>
              <Button
                variant="outlined"
                onClick={handleClearFilter}
                sx={{ textTransform: "none" }}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                startIcon={<SettingsIcon />}
                sx={{
                  backgroundColor: "#ff6b35",
                  "&:hover": { backgroundColor: "#e55a2b" },
                  textTransform: "none",
                }}
                disabled={
                  !dateRangeType ||
                  !reportOptions.some((report) => report.checked) ||
                  reportOptions.some(
                    (report) =>
                      report.checked &&
                      Object.keys(report.selectedField).length === 0
                  ) ||
                  (dateRangeType === "TIME_PERIOD" &&
                    selectedTimePeriods.length === 0)
                }
              >
                Apply Filter
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}
